const TIMEOUT = 'WRAPPER_TIMEOUT';
export const inWrapper = () => typeof window !== 'undefined' && window.parent !== window;
export const wrapperOrigin = () => process.env.REACT_APP_WRAPPER_ORIGIN || 'https://localhost';
export const newJobId = () => `job_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export function createWrapperClient(host, origin) {
  const pending = new Map(), subscribers = new Set();
  let sequence = 0;
  const notify = (type, payload = {}) => host.parent.postMessage({ ...payload, type }, origin);
  const receive = event => {
    if (event.source !== host.parent || event.origin !== origin || !event.data || typeof event.data !== 'object') return;
    const data = event.data;
    if (data.type === 'POS_PING') notify('POS_PONG');
    const request = pending.get(data.reqId);
    if (request && data.type === request.expected) {
      request.cleanup();
      if (data.ok) request.resolve(data);
      else request.reject(Object.assign(new Error(data.error || 'Opération impossible'), { code: data.code }));
    }
    subscribers.forEach(listener => listener(data));
  };
  host.addEventListener('message', receive);
  return {
    notify,
    subscribe(listener) { subscribers.add(listener); return () => subscribers.delete(listener); },
    request(type, payload = {}, { timeout = 15000, signal } = {}) {
      if (host.parent === host) return Promise.reject(new Error('Wrapper Android indisponible'));
      if (signal?.aborted) return Promise.reject(Object.assign(new Error('Opération annulée'), { code: 'ABORTED' }));
      const reqId = `req_${Date.now()}_${++sequence}`;
      return new Promise((resolve, reject) => {
        let timer;
        const cleanup = () => { clearTimeout(timer); pending.delete(reqId); signal?.removeEventListener('abort', abort); };
        const abort = () => { cleanup(); reject(Object.assign(new Error('Opération annulée'), { code: 'ABORTED' })); };
        pending.set(reqId, { resolve, reject, cleanup, expected: type + '_DONE' });
        timer = setTimeout(() => { cleanup(); reject(Object.assign(new Error(`Délai dépassé (${type})`), { code: TIMEOUT })); }, timeout);
        signal?.addEventListener('abort', abort, { once: true });
        try { notify(type, { ...payload, reqId }); } catch (error) { cleanup(); reject(error); }
      });
    },
    dispose() {
      host.removeEventListener('message', receive);
      for (const request of [...pending.values()]) { request.cleanup(); request.reject(Object.assign(new Error('Connexion fermée'), { code: 'ABORTED' })); }
      subscribers.clear();
    },
  };
}

let client, capabilities;
export function getWrapperClient() { if (!client) client = createWrapperClient(window, wrapperOrigin()); return client; }
export function getWrapperCapabilities() {
  if (!inWrapper()) return Promise.resolve({ protocolVersion: 0, capabilities: [] });
  if (!capabilities) capabilities = getWrapperClient().request('POS_HELLO', {}, { timeout: 1800 }).catch(error => {
    if (error.code === TIMEOUT) return { protocolVersion: 1, capabilities: [] };
    capabilities = null; throw error;
  });
  return capabilities;
}

export function readPrinter(storage = localStorage) {
  try {
    const saved = JSON.parse(storage.getItem('printerConfig'));
    if (saved && ['sunmi', 'bluetooth'].includes(saved.type)) return saved;
  } catch { /* Migrate the two legacy keys below. */ }
  const address = storage.getItem('printerMac');
  return address ? { type: 'bluetooth', address, name: storage.getItem('printerName') || '', paperWidth: 58 } : null;
}
export function savePrinter(printer, storage = localStorage) {
  if (printer) storage.setItem('printerConfig', JSON.stringify(printer)); else storage.removeItem('printerConfig');
  if (printer?.type === 'bluetooth') storage.setItem('printerMac', printer.address); else storage.removeItem('printerMac');
  if (printer) storage.setItem('printerName', printer.name || 'SUNMI'); else storage.removeItem('printerName');
}
export function printerState(printer = readPrinter()) {
  return { printer, printerMac: printer?.type === 'bluetooth' ? printer.address : '', printerName: printer?.name || (printer?.type === 'sunmi' ? 'SUNMI' : '') };
}

const printing = new Map();
export function printReceipt(job, options = {}) {
  const fingerprint = JSON.stringify(job);
  if (printing.has(fingerprint)) return printing.get(fingerprint);
  const promise = (async () => {
    const bridge = getWrapperClient(), caps = await getWrapperCapabilities();
    const printer = job.printer?.type ? job.printer : job.printerMac ? { ...readPrinter(), type: 'bluetooth', address: job.printerMac } : readPrinter();
    if (caps.protocolVersion < 2 && printer?.type === 'sunmi') throw new Error('Installez le nouveau wrapper pour imprimer sur SUNMI');
    const jobId = job.jobId || newJobId();
    const prepared = { ...job, jobId, printer: { ...job.printer, ...printer }, printerMac: printer?.type === 'bluetooth' ? printer.address : undefined };
    try { return await bridge.request('POS_PRINT', { job: prepared }, { ...options, timeout: caps.protocolVersion >= 2 ? 90000 : 25000 }); }
    catch (error) {
      if (error.code === TIMEOUT) {
        if (caps.protocolVersion >= 2) {
          const status = await bridge.request('POS_PRINT_STATUS', { jobId }, { timeout: 3000 }).catch(() => null);
          if (status && ['printed', 'sent'].includes(status.status)) return status;
        }
        throw Object.assign(new Error('Résultat inconnu : vérifiez le ticket avant de réimprimer.'), { code: 'PRINT_UNKNOWN' });
      }
      throw error;
    }
  })();
  printing.set(fingerprint, promise);
  promise.then(() => printing.delete(fingerprint), () => printing.delete(fingerprint));
  return promise;
}
